/**
 * RailScoreboard — bridge wave-2 item 6: the right rail's data-honest
 * scoreboard. Five stats derived PURELY in the component body from the
 * runs + escalations props — no fetch, no fabrication, and deliberately NO
 * token/cost row (the rail reports outcomes, not spend).
 *
 * CONSUMERS:
 * - frontend/src/pages/FleetHud.tsx: right rail, inside
 *   <RailBlock title="RECENT"> with hideHeader (the block supplies the title)
 *
 * Stat derivation (every value traceable to the prop shape — see
 * frontend/src/services/fleetHudApi.ts):
 * - mergedToday: runs whose status is a terminal-success ('completed' /
 *   'completed_with_warnings' — runs have no literal 'merged' status; see the
 *   terminal-status list in backend/app/api/v1/fleet_hud.py) AND whose
 *   completed_at ?? created_at falls on today (local), summing each run's
 *   items_summary entries that carry a commit.
 * - healed / rolledBack: run counts with status 'healed' / 'rolled_back'.
 * - openEscalations: escalations not explicitly resolved (resolved flag or
 *   status resolved/closed).
 * - lastJournalAge: humanized delta from max(run.journal_updated_at) to now,
 *   or '—' when no run carries the field (it is not in the current backend
 *   projection — honest absence, never a fabricated fallback).
 *
 * Motion contract: numeric values count up over 600 ms via a linear rAF loop
 * ONLY when the derived value actually changes between renders (prev tracked
 * in a ref) — never on initial mount, so no motion is unbound to a real data
 * event. Under prefers-reduced-motion the value updates instantly with zero
 * animation. Changed values flash the #FF6600 brand accent while counting. // design-law-allow
 *
 * Chrome: squared — no Tailwind rounding classes anywhere in this file.
 */
import React, { useEffect, useRef, useState } from 'react';
import { FleetEscalation, FleetRun } from '../../services/fleetHudApi';
import { formatElapsedSeconds, parseTs } from '../fleet/fleetTime';

interface RailScoreboardProps {
  runs: FleetRun[];
  escalations: FleetEscalation[];
  /** Fallback journal age in seconds from the OVERVIEW poll — the /runs
      projection doesn't carry journal_updated_at, so without this the row
      read '—' while the status card showed a real age (owner, 2026-08-14).
      Same real data, different endpoint; still never fabricated. */
  journalAgeSeconds?: number | null;
  /** Inside a RailBlock the block renders the RECENT title — suppress ours. */
  hideHeader?: boolean;
  className?: string;
}

const COUNT_UP_MS = 600;

// Terminal-success run statuses (fleet_hud.py ACTIVE_STATUSES comment):
// a run whose items merged ends in one of these — never a literal 'merged'.
const MERGED_RUN_STATUSES = new Set(['completed', 'completed_with_warnings']);

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function isSameLocalDay(ms: number, ref: Date): boolean {
  const d = new Date(ms);
  return (
    d.getFullYear() === ref.getFullYear() &&
    d.getMonth() === ref.getMonth() &&
    d.getDate() === ref.getDate()
  );
}

function isResolved(e: FleetEscalation): boolean {
  return e.resolved === true || e.status === 'resolved' || e.status === 'closed';
}

/**
 * Count-up renderer for one numeric stat. Animates ONLY on value change:
 * the ref starts at the initial value, so the mount effect is a no-op and
 * no animation ever fires unbound to a real data change.
 */
function CountUpValue({ value }: { value: number }) {
  const [display, setDisplay] = useState(value);
  const [animating, setAnimating] = useState(false);
  const prevRef = useRef(value);

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = value;
    if (prev === value) return; // initial mount + unchanged renders: no motion
    if (prefersReducedMotion() || typeof window.requestAnimationFrame !== 'function') {
      // Reduced motion: instant update, zero animation.
      setDisplay(value);
      return;
    }
    setAnimating(true);
    const start = performance.now();
    let raf = 0;
    const tick = (nowMs: number) => {
      const t = Math.min(1, (nowMs - start) / COUNT_UP_MS); // linear
      setDisplay(Math.round(prev + (value - prev) * t));
      if (t < 1) {
        raf = window.requestAnimationFrame(tick);
      } else {
        setAnimating(false);
      }
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [value]);

  return <span className={animating ? 'text-brand' : undefined}>{display}</span>;
}

export default function RailScoreboard({
  runs,
  escalations,
  journalAgeSeconds = null,
  hideHeader = false,
  className,
}: RailScoreboardProps) {
  const now = new Date();

  const mergedToday = runs
    .filter((r) => r.status !== undefined && MERGED_RUN_STATUSES.has(r.status))
    .filter((r) => {
      const ms = parseTs(r.completed_at ?? r.created_at);
      return ms !== null && isSameLocalDay(ms, now);
    })
    .reduce((sum, r) => sum + (r.items_summary ?? []).filter((it) => !!it.commit).length, 0);
  const healed = runs.filter((r) => r.status === 'healed').length;
  const rolledBack = runs.filter((r) => r.status === 'rolled_back').length;
  const openEscalations = escalations.filter((e) => !isResolved(e)).length;
  const journalMs = runs
    .map((r) => parseTs(r.journal_updated_at))
    .filter((ms): ms is number => ms !== null);
  const lastJournalAge =
    journalMs.length > 0
      ? formatElapsedSeconds((now.getTime() - Math.max(...journalMs)) / 1000)
      : journalAgeSeconds !== null && journalAgeSeconds !== undefined
        ? formatElapsedSeconds(journalAgeSeconds)
        : '—';

  const numericStats = [
    { key: 'merged-today', label: 'Merged today', value: mergedToday },
    { key: 'healed', label: 'Healed', value: healed },
    { key: 'rolled-back', label: 'Rolled back', value: rolledBack },
    { key: 'open-escalations', label: 'Open esc', value: openEscalations },
  ];

  return (
    <div
      data-testid="rail-scoreboard"
      className={`flex flex-col bg-surface${className ? ` ${className}` : ''}`}
    >
      {!hideHeader && (
        <h2 className="border-b border-white/[0.06] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-text-muted">
          RECENT
        </h2>
      )}
      <div className="flex flex-col divide-y divide-white/[0.06]">
        {numericStats.map((s) => (
          <div key={s.key} className="flex items-baseline justify-between gap-2 px-3 py-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-text-muted">
              {s.label}
            </span>
            <span
              data-testid={`stat-${s.key}-value`}
              className="text-right font-mono text-sm tabular-nums text-text-primary"
            >
              <CountUpValue value={s.value} />
            </span>
          </div>
        ))}
        <div className="flex items-baseline justify-between gap-2 px-3 py-1.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-text-muted">
            Last journal
          </span>
          <span
            data-testid="stat-journal-age-value"
            className="text-right font-mono text-sm tabular-nums text-text-primary"
          >
            {lastJournalAge}
          </span>
        </div>
      </div>
    </div>
  );
}
